// **الرقم يُضغط فيتّصل** (قرار المالك 22/09/2026: «لما يظهر الرقم ويضغط عليه يجيبله
// اتصال أو رسالة واتساب»).
//
// والبياعُ يقرأ الرقم على الشاشة ثم يحفظه في رأسه ويكتبه في التليفون — خطوتان يضيع
// بينهما رقمٌ كل يوم. فالرقمُ هنا زرّ: ضغطةٌ تفتح خيارَين، ولا شيء يُنسخ ولا يُكتب.
//
// **ولا يُتّصل بضغطةٍ واحدة**: الضغطةُ الأولى تسأل، والثانية تفعل. فالرقم يقع في وسط
// قائمةٍ تُمرَّر بالإصبع، ومكالمةٌ تبدأ بلمسةٍ عابرة إحراجٌ للبياع وللعميل.
//
// **والواتساب يحتاج صيغةً دولية** (`201…`) بخلاف الاتصال الذي يقبل المحلّي. فالرقم
// يُطبَّع هنا مرّةً: ما بدأ بـ`+` يُؤخذ كما هو، والمصريُّ المحلّي (`01…`) يُسبق بـ20.
// وما لم يُعرف شكلُه لا يُخترع له مقدّمة — يُعرض الاتصال وحده.
import { useEffect, useRef, useState } from 'react';
import { displayPhone } from '../lib/format.ts';

/** الرقم كما يُطلب في تطبيق الهاتف: أرقامٌ و`+` فقط. */
const dialable = (raw: string): string => raw.replace(/[^\d+]/g, '');

/**
 * الرقم بصيغةٍ دولية بلا `+` — كما يطلبه رابط واتساب. و`null` لما لا تُعرف دولتُه،
 * فلا تُخترع له مقدّمةٌ تفتح محادثةً مع شخصٍ آخر.
 */
export function waNumber(raw: string): string | null {
  const s = dialable(raw);
  if (s.startsWith('+')) return s.slice(1);
  if (/^00\d{8,}$/.test(s)) return s.slice(2);
  if (/^01\d{9}$/.test(s)) return `20${s.slice(1)}`;      // محلّي مصري
  if (/^\d{11,15}$/.test(s)) return s;                    // دوليٌّ مكتوبٌ بلا +
  return null;
}

/**
 * رقمٌ يُضغط. يُعرض كما يُعرض في كل الشاشات (`displayPhone`)، وتحته خيارا الاتصال
 * والواتساب.
 *
 * @param label نصٌّ بديل يُعرض مكان الرقم — لمن يريد «اتصال» لا الرقم نفسه.
 */
export function PhoneActions({ phone, className = '', label }: {
  phone: string | null | undefined;
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc); };
  }, [open]);

  if (!phone || phone.trim() === '') return <span className={className}>—</span>;

  const shown = label ?? displayPhone(phone);
  const wa = waNumber(phone);

  return (
    <span ref={box} className="relative inline-block">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        aria-label={`${displayPhone(phone)} — اتصال أو واتساب`}
        className={`rounded px-0.5 underline decoration-dotted underline-offset-2 transition-colors
          hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${className}`}>
        {shown}
      </button>
      {open && (
        <span role="menu" aria-label="طرق التواصل"
          className="absolute z-50 mt-1 flex min-w-[9rem] flex-col overflow-hidden rounded-xl border
            border-slate-200 bg-white py-1 text-start shadow-lg">
          <a role="menuitem" href={`tel:${dialable(phone)}`} onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-800 hover:bg-slate-50">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true" className="h-4 w-4 text-brand-700">
              <path d="M6.5 3.8h3l1.4 3.6-2 1.3a11 11 0 0 0 5.4 5.4l1.3-2 3.6 1.4v3a1.7 1.7 0 0 1-1.9 1.7A15.5 15.5 0 0 1 4.8 5.7 1.7 1.7 0 0 1 6.5 3.8z" />
            </svg>
            اتصال
          </a>
          {wa !== null && (
            <a role="menuitem" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-800 hover:bg-slate-50">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true" className="h-4 w-4 text-emerald-600">
                <path d="M3.8 20.2l1.2-4a8.2 8.2 0 1 1 3 3z" /><path d="M8.6 9.2c.4 2.6 3.6 5.8 6.2 6.2" />
              </svg>
              واتساب
            </a>
          )}
        </span>
      )}
    </span>
  );
}
